import React from 'react';
import { Dropdown, Space, Typography, MenuProps } from 'antd';
import { BankOutlined, DownOutlined, CheckOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useFactory } from '../store/factory';
import { useRole } from '../store/role';
import './TopBar.css';

const { Text } = Typography;

interface UserInfo { email: string; role: string; }

interface TopBarProps {
  user: UserInfo | null;
}

export default function TopBar({ user }: TopBarProps) {
  const { factories, activeFactory, switchFactory } = useFactory();
  const { role, setRole } = useRole();
  const { t } = useTranslation();
  const qc = useQueryClient();

  // ── Factory dropdown ────────────────────────────────────────────────────────
  const factoryItems: MenuProps['items'] = factories.map((f) => ({
    key: String(f.factory_id),
    label: (
      <Space>
        {f.factory_id === activeFactory?.factory_id && <CheckOutlined style={{ color: '#3b82f6' }} />}
        <span style={{ marginInlineStart: f.factory_id === activeFactory?.factory_id ? 0 : 18 }}>
          {f.name}
        </span>
      </Space>
    ),
    onClick: async () => {
      if (f.factory_id === activeFactory?.factory_id) return;
      await switchFactory(f.factory_id);
      qc.invalidateQueries();
    },
  }));

  return (
    <div className="tb" dir="ltr">
      {/* Factory selector — always on the physical left, regardless of language direction */}
      <Dropdown menu={{ items: factoryItems }} trigger={['click']} disabled={factories.length <= 1}>
        <div className="tb-factory" role="button">
          <span className="tb-factory-icon"><BankOutlined /></span>
          <div className="tb-factory-text">
            <Text strong style={{ fontSize: 14, color: '#f2f2f0' }}>{activeFactory?.name ?? 'No factory'}</Text>
            {factories.length > 1 && <span className="tb-factory-count">{factories.length} {factories.length === 1 ? 'plant' : 'plants'}</span>}
          </div>
          {factories.length > 1 && <DownOutlined className="tb-chevron" />}
        </div>
      </Dropdown>

      {/* Role switcher — free demo toggle, not tied to the user's real DB role */}
      <div className="tb-role-group" role="group">
        <button
          type="button"
          className={`tb-role-btn${role === 'loader' ? ' active' : ''}`}
          onClick={() => setRole('loader')}
        >
          {t('role.loader')}
        </button>
        <button
          type="button"
          className={`tb-role-btn${role === 'technologist' ? ' active' : ''}`}
          onClick={() => setRole('technologist')}
        >
          {t('role.technologist')}
        </button>
      </div>
    </div>
  );
}
